import {formatTreatmentLabels} from '@/modules/patients-therapies/functions'
import {useTherapySequenceActions} from '@/modules/patients-therapies/hooks'
import {usePreparationUseBy} from '@/modules/patients-therapies/hooks/use-preparation-use-by'
import {Box, Button, ButtonGroup, HStack, Text} from '@chakra-ui/react'
import {AdministrationMode} from '@cypro/engine/database/schema'
import {PatientTherapySequence} from '@cypro/engine/schema'
import {DialogBody, DialogFooter, DialogHeader} from '@cypro/ui/components'
import {getShortDateFormatter, isDefined} from '@cypro/ui/utils'
import {useTranslate} from '@tolgee/react'
import {type ReactNode, useState} from 'react'
import {PreparationExpiredAlert, UseByValue} from '../preparation-use-by/preparation-use-by'
import {closeManualAdministerDialog} from './util'

/** Manual administration mode: the nurse checks the patient and preparation details, then confirms (CY-212). */
export function ManualAdministerDialog({sequence}: {sequence: PatientTherapySequence}) {
  const {t} = useTranslate('common')
  const {sendEvent} = useTherapySequenceActions(sequence)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const {names, dosages} = formatTreatmentLabels(sequence, t)
  const patient = sequence.patient
  const {useBy, isExpired} = usePreparationUseBy(sequence.id)

  async function onConfirm() {
    setIsSubmitting(true)
    try {
      const res = await sendEvent({
        id: sequence.id,
        event: {type: 'ADMINISTER', body: {completed: true, mode: AdministrationMode.enum.MANUAL}},
      })
      if (res?.type !== 'error') closeManualAdministerDialog()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <DialogHeader>{t('manual_administration.title', 'Administer prescription')}</DialogHeader>

      <DialogBody minHeight="none" spaceY="3">
        <Box
          borderWidth="1px"
          borderColor="border"
          borderRadius="md"
          bg="bg.subtle"
          paddingX="3"
          paddingY="2"
          spaceY="2"
        >
          <SummaryLine label={t('patient')} value={patient?.fullName} />
          <SummaryLine
            label={t('birth_date')}
            value={isDefined(patient?.birthDate) ? getShortDateFormatter().format(new Date(patient.birthDate)) : null}
          />
          <SummaryLine label={t('hospital_id')} value={patient?.hospitalPatientId} />
          <SummaryLine label={t('substance')} value={names.join(' / ')} />
          <SummaryLine label={t('dose')} value={dosages.join(' / ')} />
          <SummaryLine label={t('production_number', 'Production number')} value={sequence.internalId} />
          <SummaryLine label={t('use_by')} value={<UseByValue useBy={useBy} />} />
        </Box>
        {isExpired && <PreparationExpiredAlert />}
        <Text textStyle="sm" color="fg.muted">
          {t(
            'manual_administration.reminder',
            'Check that the patient and the preparation match these details before administering.'
          )}
        </Text>
      </DialogBody>

      <DialogFooter>
        <ButtonGroup size="md" width="100%" justifyContent="flex-end">
          <Button variant="outline" colorPalette="gray" onClick={closeManualAdministerDialog} disabled={isSubmitting}>
            {t('cancel')}
          </Button>
          <Button colorPalette="cyan" onClick={onConfirm} loading={isSubmitting} disabled={isExpired}>
            {t('mark_as_administer')}
          </Button>
        </ButtonGroup>
      </DialogFooter>
    </>
  )
}

function SummaryLine({label, value}: {label: string; value?: ReactNode}) {
  return (
    <HStack alignItems="baseline">
      <Text textStyle="xs" color="fg.muted">
        {label}:
      </Text>
      <Text textStyle="xs" fontWeight="medium">
        {value || '-'}
      </Text>
    </HStack>
  )
}
